import { FC, Key, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { InputRef } from 'antd';
import { saveAs } from 'file-saver';
import { TbPaperclip } from 'react-icons/tb';
import { toast } from '@/shared/components/toasts/PlatformToast';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useDismissibleSelection } from '@/hooks/useDismissibleSelection';
import { useSlashSearchFocus } from '@/hooks/useSlashSearchFocus';
import {
    buildListFilterExpression,
    ListFilterRule,
} from '@/layouts/02-Authenticated/layout/03-Content/routes/08-Relations/layout/03-Sections/views/02-Lists/routes/ListDetail/layout/03-FiltersRow/components/01-FilterDropdown';
import {
    AttachmentDestination,
    CloudAttachment,
    CloudStorageConnection,
    downloadCloudAttachments,
    getCloudAttachments,
    moveCloudAttachment,
    moveCloudAttachments,
    relocateCloudAttachments,
} from '@/services';
import { evaluateJsonLogicRule, isJsonLogicRuleEmpty } from '@/shared/components/filters/jsonLogic';
import LearnMoreFooter from '@/shared/components/layouts/footers/LearnMoreFooter';
import { EmptyView } from '@/shared/components/layouts/views/EmptySection';
import CreateAttachmentFolderModal from '@/shared/components/modals/ResourceCreationModals/CreateAttachmentFolderModal';

import AttachmentBulkActions from '../AttachmentBulkActions';
import AttachmentDocumentDrawer from '../AttachmentDocumentDrawer';
import AttachmentDocumentView, { AttachmentDocumentMode } from '../AttachmentDocumentView';
import AttachmentFileList from '../AttachmentFileList';
import { getAffectedAttachmentFolderIds } from '../AttachmentFileList/attachmentTree';
import AttachmentFiltersRow from '../AttachmentFiltersRow';
import AttachmentPanelToolbar from '../AttachmentPanelToolbar';

interface ResourceAttachmentsPanelProps {
    canComment?: boolean;
    canUpload: boolean;
    destination: AttachmentDestination;
    documentPresentation?: 'drawer' | 'inline';
    onDocumentStateChange?: (state?: AttachmentDocumentState) => void;
    onResourceBreadcrumbNavigate?: () => void;
    resourceBreadcrumbLabel?: string;
    resourceBreadcrumbPath?: string;
    resourceLabel: string;
}

export interface AttachmentDocumentState {
    attachment: CloudAttachment;
    mode: AttachmentDocumentMode;
}

const ResourceAttachmentsPanel: FC<ResourceAttachmentsPanelProps> = ({
    canComment,
    canUpload,
    destination,
    documentPresentation = 'drawer',
    onDocumentStateChange,
    onResourceBreadcrumbNavigate,
    resourceBreadcrumbLabel,
    resourceBreadcrumbPath,
    resourceLabel,
}) => {
    const { token } = useAntDesignTheme();
    const [connection, setConnection] = useState<Partial<CloudStorageConnection>>({});
    const [filters, setFilters] = useState<ListFilterRule[]>([]);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [createFolderOpen, setCreateFolderOpen] = useState(false);
    const [files, setFiles] = useState<CloudAttachment[]>([]);
    const [loading, setLoading] = useState(false);
    const [movingAttachmentIds, setMovingAttachmentIds] = useState<ReadonlySet<string>>(() => new Set());
    const [syncingFolderIds, setSyncingFolderIds] = useState<ReadonlySet<string>>(() => new Set());
    const [query, setQuery] = useState('');
    const [selectedAttachment, setSelectedAttachment] = useState<CloudAttachment>();
    const [selectedKeys, setSelectedKeys] = useState<ReadonlySet<Key>>(() => new Set());
    const bulkActionRef = useRef(false);
    const movingAttachmentIdsRef = useRef(new Set<string>());
    const syncingFolderCountsRef = useRef(new Map<string, number>());
    const searchInputRef = useRef<InputRef>(null);
    const attachmentDestination = useMemo<AttachmentDestination>(
        () => ({
            listName: destination.listName,
            recordName: destination.recordName,
            resourceId: destination.resourceId,
            resourceName: destination.resourceName,
            resourceType: destination.resourceType,
        }),
        [
            destination.listName,
            destination.recordName,
            destination.resourceId,
            destination.resourceName,
            destination.resourceType,
        ],
    );

    const loadFiles = useCallback(async () => {
        if (!connection.id || !connection.type || !attachmentDestination.resourceId) {
            setFiles([]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setFiles(await getCloudAttachments({ id: connection.id, type: connection.type }, attachmentDestination));
        } catch (error) {
            captureException(error);
            setFiles([]);
        } finally {
            setLoading(false);
        }
    }, [attachmentDestination, connection.id, connection.type]);

    useEffect(() => {
        void loadFiles();
    }, [loadFiles]);

    useEffect(() => {
        setSelectedAttachment(undefined);
        onDocumentStateChange?.(undefined);
    }, [attachmentDestination.resourceId, connection.id, onDocumentStateChange]);

    const filterExpression = useMemo(() => buildListFilterExpression(filters), [filters]);
    const filteredFiles = useMemo(() => {
        const normalizedQuery = query.trim().toLowerCase();

        return files.filter((file) => {
            const matchesQuery =
                !normalizedQuery ||
                [file.name, file.relativePath, file.mimeType].some((value) =>
                    value?.toLowerCase().includes(normalizedQuery),
                );
            const createdTime = file.createdTime ? new Date(file.createdTime) : undefined;
            const matchesFilters =
                isJsonLogicRuleEmpty(filterExpression) ||
                evaluateJsonLogicRule(filterExpression, {
                    createdTime:
                        createdTime && !Number.isNaN(createdTime.getTime())
                            ? createdTime.toISOString().slice(0, 10)
                            : '',
                    kind: file.kind ?? 'file',
                    mimeType: file.mimeType,
                    name: file.name,
                    relativePath: file.relativePath ?? '',
                    size: Number(file.size ?? 0),
                });

            return matchesQuery && matchesFilters;
        });
    }, [files, filterExpression, query]);
    const attachmentTypeOptions = useMemo(
        () =>
            Array.from(
                new Map(
                    files
                        .filter((file) => file.kind !== 'folder')
                        .map((file) => [file.mimeType, { label: file.mimeType, value: file.mimeType }] as const),
                ).values(),
            ).sort((left, right) => left.label.localeCompare(right.label)),
        [files],
    );

    const selectedConnection = useMemo(
        () =>
            connection.id && connection.type
                ? { id: connection.id, label: connection.label, type: connection.type }
                : undefined,
        [connection.id, connection.label, connection.type],
    );
    const visibleAttachments = useMemo(() => filteredFiles.filter((file) => file.kind !== 'folder'), [filteredFiles]);
    const selectedAttachments = useMemo(
        () => files.filter((file) => file.kind !== 'folder' && selectedKeys.has(file.id)),
        [files, selectedKeys],
    );
    const clearSelection = useCallback(() => setSelectedKeys(new Set()), []);
    const closeSelectedAttachment = useCallback(() => {
        setSelectedAttachment(undefined);
        onDocumentStateChange?.(undefined);
    }, [onDocumentStateChange]);
    const navigateToResource = useCallback(() => {
        closeSelectedAttachment();
        onResourceBreadcrumbNavigate?.();
    }, [closeSelectedAttachment, onResourceBreadcrumbNavigate]);
    const handleSelectedConnectionChange: typeof setConnection = useCallback(
        (nextConnection) => {
            clearSelection();
            setConnection(nextConnection);
        },
        [clearSelection],
    );

    useSlashSearchFocus(searchInputRef, Boolean(selectedConnection && files.length), { priority: 30 });

    useDismissibleSelection({
        active: selectedKeys.size > 0,
        onDismiss: clearSelection,
    });

    const updateSyncingFolders = useCallback((folderIds: ReadonlySet<string>, increment: boolean) => {
        folderIds.forEach((folderId) => {
            const nextCount = (syncingFolderCountsRef.current.get(folderId) ?? 0) + (increment ? 1 : -1);

            if (nextCount > 0) {
                syncingFolderCountsRef.current.set(folderId, nextCount);
            } else {
                syncingFolderCountsRef.current.delete(folderId);
            }
        });
        setSyncingFolderIds(new Set(syncingFolderCountsRef.current.keys()));
    }, []);

    const handleMoveAttachment = useCallback(
        async (sourceAttachmentId: string, destinationFolderId?: string) => {
            if (!selectedConnection || movingAttachmentIdsRef.current.has(sourceAttachmentId)) {
                return;
            }

            const optimisticFiles = relocateCloudAttachments(files, [sourceAttachmentId], destinationFolderId);

            if (optimisticFiles === files) {
                return;
            }

            const rollbackFiles = new Map(
                files.filter((file, index) => file !== optimisticFiles[index]).map((file) => [file.id, file] as const),
            );

            const sourceAttachment = files.find((file) => file.id === sourceAttachmentId);
            const affectedFolderIds = getAffectedAttachmentFolderIds(
                files,
                [sourceAttachmentId],
                destinationFolderId,
            );
            movingAttachmentIdsRef.current.add(sourceAttachmentId);
            setMovingAttachmentIds(new Set(movingAttachmentIdsRef.current));
            updateSyncingFolders(affectedFolderIds, true);
            setFiles(optimisticFiles);

            try {
                const movedFolder = await moveCloudAttachment(
                    selectedConnection,
                    attachmentDestination,
                    sourceAttachmentId,
                    destinationFolderId,
                );

                setFiles((current) =>
                    current.map((file) =>
                        file.id === movedFolder.id ? { ...file, relativePath: movedFolder.relativePath } : file,
                    ),
                );
                toast.success(sourceAttachment?.kind === 'folder' ? 'Attachment folder moved' : 'Attachment moved');
            } catch (error) {
                captureException(error);
                setFiles((current) => current.map((file) => rollbackFiles.get(file.id) ?? file));
                toast.error(
                    sourceAttachment?.kind === 'folder'
                        ? 'Unable to move attachment folder'
                        : 'Unable to move attachment',
                );
            } finally {
                movingAttachmentIdsRef.current.delete(sourceAttachmentId);
                setMovingAttachmentIds(new Set(movingAttachmentIdsRef.current));
                updateSyncingFolders(affectedFolderIds, false);
            }
        },
        [attachmentDestination, files, selectedConnection, updateSyncingFolders],
    );

    const handleBulkDownload = useCallback(async () => {
        if (!selectedConnection || bulkActionRef.current || !selectedAttachments.length) {
            return;
        }

        bulkActionRef.current = true;
        setBulkLoading(true);

        try {
            const archive = await downloadCloudAttachments(
                selectedConnection,
                attachmentDestination,
                selectedAttachments.map((attachment) => attachment.id),
            );

            saveAs(archive, 'countercyclical-attachments.zip');
            toast.success('Attachment download ready', {
                description: `${selectedAttachments.length} attachment${selectedAttachments.length === 1 ? '' : 's'} packaged securely.`,
            });
            clearSelection();
        } catch (error) {
            captureException(error);
            toast.error('Unable to download selected attachments');
        } finally {
            bulkActionRef.current = false;
            setBulkLoading(false);
        }
    }, [attachmentDestination, clearSelection, selectedAttachments, selectedConnection]);

    const handleBulkMove = useCallback(
        async (destinationFolderId?: string) => {
            if (
                !selectedConnection ||
                bulkActionRef.current ||
                movingAttachmentIdsRef.current.size > 0 ||
                !selectedAttachments.length
            ) {
                return;
            }

            const sourceAttachmentIds = selectedAttachments.map((attachment) => attachment.id);
            const optimisticFiles = relocateCloudAttachments(files, sourceAttachmentIds, destinationFolderId);

            if (optimisticFiles === files) {
                return;
            }

            const rollbackFiles = new Map(
                files.filter((file, index) => file !== optimisticFiles[index]).map((file) => [file.id, file] as const),
            );
            const affectedFolderIds = getAffectedAttachmentFolderIds(
                files,
                sourceAttachmentIds,
                destinationFolderId,
            );

            bulkActionRef.current = true;
            setBulkLoading(true);
            updateSyncingFolders(affectedFolderIds, true);
            setFiles(optimisticFiles);

            try {
                const result = await moveCloudAttachments(
                    selectedConnection,
                    attachmentDestination,
                    sourceAttachmentIds,
                    destinationFolderId,
                );
                const failedIds = new Set(result.failures.map((failure) => failure.attachmentId));
                const movedById = new Map(result.attachments.map((attachment) => [attachment.id, attachment] as const));

                setFiles((current) =>
                    current.map((file) => {
                        if (failedIds.has(file.id)) {
                            return rollbackFiles.get(file.id) ?? file;
                        }

                        const moved = movedById.get(file.id);
                        return moved ? { ...file, relativePath: moved.relativePath } : file;
                    }),
                );
                setSelectedKeys(failedIds);

                if (result.failures.length) {
                    toast.warning('Some attachments could not be moved', {
                        description: `${result.attachments.length} moved and ${result.failures.length} remain selected for retry.`,
                    });
                } else {
                    toast.success('Attachments moved', {
                        description: `${result.attachments.length} attachment${result.attachments.length === 1 ? '' : 's'} moved.`,
                    });
                }
            } catch (error) {
                captureException(error);
                setFiles((current) => current.map((file) => rollbackFiles.get(file.id) ?? file));
                toast.error('Unable to move selected attachments');
            } finally {
                bulkActionRef.current = false;
                setBulkLoading(false);
                updateSyncingFolders(affectedFolderIds, false);
            }
        },
        [attachmentDestination, files, selectedAttachments, selectedConnection, updateSyncingFolders],
    );

    if (documentPresentation === 'inline' && selectedAttachment && selectedConnection) {
        return (
            <AttachmentDocumentView
                key={selectedAttachment.id}
                attachment={selectedAttachment}
                canComment={canComment}
                connection={selectedConnection}
                destination={attachmentDestination}
                onAttachmentsBreadcrumbNavigate={closeSelectedAttachment}
                onResourceBreadcrumbNavigate={navigateToResource}
                resourceBreadcrumbLabel={resourceBreadcrumbLabel}
                resourceBreadcrumbPath={resourceBreadcrumbPath}
                onAttachmentCreated={(attachment) => {
                    setFiles((current) =>
                        current.some((candidate) => candidate.id === attachment.id)
                            ? current
                            : [...current, attachment],
                    );
                    setSelectedAttachment(attachment);
                    onDocumentStateChange?.({ attachment, mode: 'preview' });
                }}
                onClose={closeSelectedAttachment}
                onModeChange={(mode) => {
                    onDocumentStateChange?.({ attachment: selectedAttachment, mode });
                }}
            />
        );
    }

    return (
        <section style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <AttachmentPanelToolbar
                ref={searchInputRef}
                canCreateFolder={canUpload}
                connection={connection}
                filesAvailable={files.length > 0}
                onCreateFolder={() => setCreateFolderOpen(true)}
                query={query}
                setConnection={handleSelectedConnectionChange}
                setQuery={setQuery}
            />
            <AttachmentFiltersRow
                filters={filters}
                selectedKeys={selectedKeys}
                typeOptions={attachmentTypeOptions}
                visibleAttachmentIds={visibleAttachments.map((attachment) => attachment.id)}
                onChange={setFilters}
                onSelectedKeysChange={setSelectedKeys}
            />
            <div style={{ flex: '1 1 0%', minHeight: 0, overflow: 'hidden' }}>
                {selectedConnection ? (
                    <AttachmentFileList
                        bulkActions={
                            selectedAttachments.length ? (
                                <AttachmentBulkActions
                                    canMove={canUpload}
                                    folders={files.filter((file) => file.kind === 'folder')}
                                    loading={bulkLoading}
                                    selectedCount={selectedAttachments.length}
                                    visibleCount={visibleAttachments.length}
                                    onClear={clearSelection}
                                    onDownload={() => void handleBulkDownload()}
                                    onMove={(folderId) => void handleBulkMove(folderId)}
                                    onSelectAll={() =>
                                        setSelectedKeys(new Set(visibleAttachments.map((attachment) => attachment.id)))
                                    }
                                />
                            ) : null
                        }
                        canUpload={canUpload}
                        connection={selectedConnection}
                        destination={attachmentDestination}
                        files={filteredFiles}
                        height='100%'
                        loading={loading}
                        filterActive={Boolean(query || filters.length)}
                        movingAttachmentIds={movingAttachmentIds}
                        syncingFolderIds={syncingFolderIds}
                        selectedKeys={selectedKeys}
                        resourceLabel={resourceLabel}
                        onOpen={(attachment) => {
                            setSelectedAttachment(attachment);
                            if (documentPresentation === 'inline') {
                                onDocumentStateChange?.({ attachment, mode: 'preview' });
                            }
                        }}
                        onMoveAttachment={query || filters.length ? undefined : handleMoveAttachment}
                        onSelectedKeysChange={setSelectedKeys}
                        onUploaded={(attachment) =>
                            setFiles((current) =>
                                current.some((file) => file.id === attachment.id) ? current : [...current, attachment],
                            )
                        }
                    />
                ) : (
                    <EmptyView
                        description={`Select a connected cloud storage provider to upload, organize, and review files associated with this ${resourceLabel.toLowerCase()}.`}
                        height='100%'
                        icon={<TbPaperclip style={{ color: token.colorTextTertiary }} />}
                        label='Select a Connection to View Files'
                    />
                )}
            </div>
            <div
                style={{
                    borderTop: `1px solid ${token.colorBorder}`,
                    flex: 'none',
                    padding: '0.75rem 1rem',
                }}
            >
                <LearnMoreFooter align='left' topic='uploads and attachment management' />
            </div>
            <CreateAttachmentFolderModal
                destination={attachmentDestination}
                open={createFolderOpen}
                selectedConnection={connection}
                onCancel={() => setCreateFolderOpen(false)}
                onCreated={(createdInConnection, folder) => {
                    const sameConnection = createdInConnection.id === connection.id;

                    setConnection(createdInConnection);
                    setFiles((current) =>
                        sameConnection && !current.some((item) => item.id === folder.id)
                            ? [...current, folder]
                            : [folder],
                    );
                }}
            />
            {documentPresentation === 'drawer' ? (
                <AttachmentDocumentDrawer
                    key={selectedAttachment?.id}
                    attachment={selectedAttachment}
                    canComment={canComment}
                    connection={selectedConnection}
                    destination={attachmentDestination}
                    open={Boolean(selectedAttachment)}
                    onAttachmentsBreadcrumbNavigate={closeSelectedAttachment}
                    onResourceBreadcrumbNavigate={closeSelectedAttachment}
                    resourceBreadcrumbLabel={resourceBreadcrumbLabel}
                    resourceBreadcrumbPath={resourceBreadcrumbPath}
                    onAttachmentCreated={(attachment) => {
                        setFiles((current) =>
                            current.some((candidate) => candidate.id === attachment.id)
                                ? current
                                : [...current, attachment],
                        );
                        setSelectedAttachment(attachment);
                    }}
                    onClose={closeSelectedAttachment}
                />
            ) : null}
        </section>
    );
};

export default ResourceAttachmentsPanel;
